import { Divider, Skeleton, Text } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { HEADER_HEIGHT } from 'packages/header/ui/Header';
import { CollapsableSideBar } from 'packages/sidebar/ui/CollapsableSideBar';
import { SideBarHeader } from 'packages/sidebar/ui/SideBarHeader';
import { SideBarItem } from 'packages/sidebar/ui/SideBarItem';
import {
  getSideBarModeFromPath,
  SideBarMode,
} from 'packages/sidebar/ui/sideBarUtils';

export const ValleyBankSideBarRoot = withLDConsumer()(
  ({
    setSideBarMode,
    canToggleDataSource,
    isAssignmentEnabled = true,
    isAdvanceRateEnabled = true,
  }: {
    setSideBarMode: React.Dispatch<React.SetStateAction<SideBarMode>>;
    canToggleDataSource: boolean;
    isAssignmentEnabled?: boolean;
    isAdvanceRateEnabled?: boolean;
  }) => {
    const { pathname: currentPath } = useLocation();

    return (
      <CollapsableSideBar
        additionalHeaderHeight={canToggleDataSource ? HEADER_HEIGHT : undefined}
      >
        <SideBarItem
          routeName="loan-accounts"
          currentPath={currentPath}
          label="Loan Accounts"
          icon="building-columns"
        />

        <SideBarItem
          routeName="customers"
          currentPath={currentPath}
          label="Customers"
          icon="user"
        />

        <SideBarItem
          routeName="arrangement-groups"
          currentPath={currentPath}
          label="Arrangement Groups"
          icon="folder-tree"
        />

        {isAssignmentEnabled && (
          <SideBarItem
            routeName="assignment"
            currentPath={currentPath}
            label="Assignment"
            icon="sliders"
          />
        )}

        {isAdvanceRateEnabled && (
          <SideBarItem
            routeName="advance-rate"
            currentPath={currentPath}
            label="Advance Rate"
            icon="money-check-dollar"
          />
        )}

        <Divider style={{ marginTop: '24px', marginBottom: '24px' }} />

        <SideBarItem
          routeName="settings"
          currentPath={currentPath}
          label="Settings"
          icon="gear"
          onClick={() => setSideBarMode(SideBarMode.settings)}
        />
      </CollapsableSideBar>
    );
  },
);

const ValleyBankSideBarSettingsFacilityHeader: React.FC<{
  creditFacilityId: string;
}> = ({ creditFacilityId }) => {
  const {
    data: creditFacilityData,
    loading: isCreditFacilityLoading,
  } = useGetCreditFacilityQuery({ variables: { creditFacilityId } });

  if (!creditFacilityData || isCreditFacilityLoading) {
    return (
      <Skeleton
        h="600px"
        data-testid="settings-facility-sidebar-header-loading"
      />
    );
  }

  const creditFacilityName = commonUtils.creditFacilitySlugToName(
    creditFacilityData.creditFacility.slug,
  );

  return (
    <Text fontSize="14px" pl="20px" pb={4} fontWeight="semibold">
      {creditFacilityName}
    </Text>
  );
};

export const ValleyBankSideBarSettings = withLDConsumer()(() => {
  const { pathname: currentPath } = useLocation();
  const history = useHistory();

  const query = useQueryParams();
  const creditFacilityId = query.get(creditFacilityIdQueryParam);

  return (
    <CollapsableSideBar dividerStyleProps={{ mt: '6' }}>
      <SideBarHeader
        headerLabel="Exit Settings"
        onClickPrevious={() => {
          history.push('/');
        }}
      />
      <Divider style={{ marginTop: '6px', marginBottom: '12px' }} />
      <SideBarItem
        routeName="settings/my-account"
        currentPath={currentPath}
        label="My Account"
        icon="user"
        textViewMarginLeft="20px"
      />
      <SideBarItem
        currentPath={currentPath}
        label="Facilities"
        routeName="settings/facilities"
        icon="building-columns"
        strictPathMatch
        textViewMarginLeft="20px"
      />
      {/* show facility level settings if cf is selected */}
      {creditFacilityId && (
        <Divider style={{ marginTop: '12px', marginBottom: '12px' }} />
      )}
      {creditFacilityId && (
        <ValleyBankSideBarSettingsFacilityHeader
          creditFacilityId={creditFacilityId}
        />
      )}
      {creditFacilityId && (
        <SideBarItem
          routeName="settings/facilities/borrowing-base/"
          // attach the credit facility query string to each side bar item
          routeAddress={`settings/facilities/borrowing-base/configuration${`?${creditFacilityIdQueryParam}=${creditFacilityId}`}`}
          currentPath={currentPath}
          label="Borrowing Base"
          icon="money-check-dollar"
          textViewMarginLeft="20px"
        />
      )}
    </CollapsableSideBar>
  );
});

export const getSideBarForMode = ({
  canToggleDataSource,
  sideBarMode,
  setSideBarMode,
}: {
  canToggleDataSource: boolean;
  sideBarMode: SideBarMode;
  setSideBarMode: React.Dispatch<React.SetStateAction<SideBarMode>>;
}): React.ReactElement => {
  switch (sideBarMode) {
    case SideBarMode.root:
      return (
        <ValleyBankSideBarRoot
          canToggleDataSource={canToggleDataSource}
          setSideBarMode={setSideBarMode}
        />
      );
    case SideBarMode.settings:
    case SideBarMode.settingsFacility:
      return <ValleyBankSideBarSettings />;
    default:
      return (
        <ValleyBankSideBarRoot
          canToggleDataSource={canToggleDataSource}
          setSideBarMode={setSideBarMode}
        />
      );
  }
};

export const ValleyBankSideBar: React.FC<{
  canToggleDataSource: boolean;
  isAssignmentEnabled?: boolean;
  isAdvanceRateEnabled?: boolean;
}> = withLDConsumer()(
  ({
    canToggleDataSource,
    isAssignmentEnabled,
    isAdvanceRateEnabled,
    flags,
  }) => {
    const { pathname: currentPath } = useLocation();

    const [sideBarMode, setSideBarMode] = useState<SideBarMode>(
      SideBarMode.root,
    );

    useEffect(() => {
      const currrentSideBarMode = getSideBarModeFromPath({ currentPath });
      setSideBarMode(currrentSideBarMode);
    }, [currentPath]);

    return flags?.displayLenderSettingsFacilities ? (
      getSideBarForMode({ sideBarMode, setSideBarMode, canToggleDataSource })
    ) : (
      <ValleyBankSideBarRoot
        setSideBarMode={setSideBarMode}
        canToggleDataSource={canToggleDataSource}
        isAssignmentEnabled={isAssignmentEnabled}
        isAdvanceRateEnabled={isAdvanceRateEnabled}
      />
    );
  },
);
